import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, ShoppingBag } from "lucide-react";
import { SizePicker } from "./SizePicker";
import { SizeChart } from "./SizeChart";
import { money } from "./useProducts";
import { StockMeter } from "./StockMeter";
import { PalestineCauseBanner, isPalestine } from "./PalestineCauseBanner";
import { useCart } from "./cart/CartContext";
import { PayPalButton } from "./cart/PayPalButton";
import { PreOrderNotice } from "./cart/PreOrderNotice";

export const PreOrderPanel = ({ product, color, onColorChange }) => {
  const [option, setOption] = useState(product.options[0]);
  const [size, setSize] = useState(null);
  const [added, setAdded] = useState(false);
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const chosenSize = option.sized ? size : "ONE SIZE";
  const soldOut = product.remaining === 0;

  const line = () => ({
    lookupKey: option.lookupKey, productId: product.id, productSlug: product.slug, productName: product.name, optionLabel: option.label,
    size: chosenSize, color: color?.label || null, price: option.price, regular: option.regular || null, image: product.image, category: product.category, quantity: 1,
  });

  const addToBag = () => {
    if (option.sized && !size) {
      toast.error("Pick your size first.");
      return false;
    }
    add(line());
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
    return true;
  };

  const buyNow = () => {
    if (addToBag()) navigate("/cart");
  };

  return (
    <div data-testid="preorder-panel" className="mt-6">
      <StockMeter product={product} testId="quickview-stock" />
      {isPalestine(product) && <div className="mt-5"><PalestineCauseBanner compact testId="palestine-cause-banner-quickview" /></div>}
      {product.earlyBeliever && (
        <div data-testid="early-believer-banner" className="mb-6 flex items-center justify-between gap-3 rounded-2xl bg-[#0a0a0b] text-white px-4 py-3">
          <span className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Early Believer price</span>
          <span className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-white/60">{product.edition}</span>
        </div>
      )}

      {product.colors?.length > 0 && (
        <>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">
            Colour <span className="text-[#0a0a0b]">— {color.label}</span>
          </p>
          <div data-testid="color-picker" className="flex gap-3 mb-6">
            {product.colors.map((c) => {
              const active = c.id === color.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`color-${c.id}`}
                  aria-label={c.label}
                  onClick={() => onColorChange(c)}
                  className={`h-9 w-9 rounded-full border-2 transition-all duration-300 ${active ? "border-[#ff2a85] scale-110" : "border-[#e3e2dc] hover:border-[#0a0a0b]"}`}
                  style={{ backgroundColor: c.hex }}
                />
              );
            })}
          </div>
        </>
      )}

      {product.options.length > 1 && (
        <>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">{product.category === "accessories" ? "Choose piece" : "Buy as"}</p>
          <div data-testid="option-picker" className="grid gap-2 mb-6 grid-cols-1">
            {product.options.map((o) => {
              const active = o.id === option.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`option-${o.id}`}
                  onClick={() => setOption(o)}
                  className={`min-w-0 rounded-2xl border px-3 py-3 text-left transition-all duration-300 flex items-center justify-between gap-3 ${
                    active ? "border-[#0a0a0b] bg-[#0a0a0b] text-white" : "border-[#e3e2dc] bg-[#ffffff] hover:border-[#ff2a85]"
                  }`}
                >
                  <span className="block min-w-0">
                    <span className="block font-display font-extrabold uppercase text-[11px] leading-tight">{o.label}</span>
                    <span className={`block font-mono-d text-[9px] uppercase tracking-[0.12em] mt-1 ${active ? "text-white/60" : "text-[#5f5e5a]"}`}>{o.sub}</span>
                  </span>
                  <span className="shrink-0 flex items-baseline gap-1.5">
                    <span className={`font-mono-d text-sm ${active ? "text-[#ff2a85]" : "text-[#0a0a0b]"}`}>{money(o.price)}</span>
                    {o.regular && <span className={`font-mono-d text-[10px] line-through ${active ? "text-white/40" : "text-[#5f5e5a]"}`}>{money(o.regular)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {option.sized ? (
        <>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">
            Select size {size && <span className="text-[#0a0a0b]">— {size}</span>}
          </p>
          <SizePicker value={size} onChange={setSize} testPrefix="quickview-size" compact />
          {product.sizeChart?.length > 0 && <SizeChart parts={option.id === "hoodie" ? ["hoodie"] : option.id === "pants" ? ["pants"] : product.sizeChart} />}
        </>
      ) : (
        <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">One size · Fits all</p>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          data-testid="quickview-add-to-bag"
          onClick={() => { if (addToBag()) setOpen(true); }}
          disabled={soldOut}
          className="w-full px-5 py-4 rounded-full border border-[#0a0a0b] bg-[#ffffff] text-[#0a0a0b] font-mono-d text-[11px] uppercase tracking-[0.2em] hover:bg-[#0a0a0b] hover:text-white disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        >
          {soldOut ? "Sold Out" : added ? <><Check size={14} strokeWidth={3} /> Added to bag</> : <><ShoppingBag size={14} /> Add to bag</>}
        </button>
        <button
          data-testid="quickview-preorder-button"
          onClick={buyNow}
          disabled={soldOut}
          className="w-full px-5 py-4 rounded-full bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] hover:bg-[#e60067] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        >
          {soldOut ? "Sold Out" : (
            <>
              Buy now — {money(option.price)}
              {option.regular && <span className="line-through text-white/50 normal-case tracking-normal">{money(option.regular)}</span>}
            </>
          )}
        </button>
      </div>
      <div className="mt-2">
        <PayPalButton
          testId="quickview-paypal-button"
          disabled={soldOut}
          build={() => {
            if (option.sized && !size) { toast.error("Pick your size first."); return null; }
            return { items: [{ lookup_key: option.lookupKey, size: chosenSize, color: color?.label || null, quantity: 1 }], shipping_rate_id: null, destination: localStorage.getItem("dd_destination") || "us", coupon_code: null };
          }}
        />
      </div>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-[10px] font-mono-d uppercase tracking-[0.15em] text-[#5f5e5a]">
        <Lock size={11} /> Stripe · PayPal · Apple Pay · USD · Ships worldwide
      </p>
      <PreOrderNotice compact />
    </div>
  );
};
